import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpParams, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {getToken, IAuthenticationState, LogoutSuccess} from '@app/stores';
import {catchError, switchMap, take, tap} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {LoaderService} from '@app/services/loader.service';
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from '@app/services/authentication.service';
import {ReportAccessTokenService} from '@app/services/report-access-token.service';
import {SessionTimeoutService} from '@app/services/session-timeout.service';
import {AppVersionService} from '@app/services/app-version.service';
import {MaintenanceService} from '@app/services/maintenance.service';

@Injectable()
export class HttpAuthInterceptService implements HttpInterceptor {
  inflightAuthRequest = null;
  currentRoute = '/';

  constructor(
    private authentication: Store<IAuthenticationState>,
    private router: Router,
    private toastr: ToastrService,
    private loaderService: LoaderService,
    private authenticationService: AuthenticationService,
    private reportAccessTokenService: ReportAccessTokenService,
    private sessionTimeoutService?: SessionTimeoutService,
    private appVersionService?: AppVersionService,
    private maintenanceService?: MaintenanceService
  ) {
    router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        if (!this.router.url.includes('/login')) {
          this.currentRoute = this.router.url;
        }
      }
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // A static file under /assets is not an API call: it needs no token, and pulling it through
    // the refresh-and-retry path below couples it to session state. That is how the translation
    // dictionary came to be queued behind a failing /auth/refresh and never loaded at all.
    if (/^\/?assets\//.test(request.url)) {
      return next.handle(request);
    }

    let hasSkipOAuth: boolean = request.headers.has('Skip-auth');
    const useTempToken: boolean = request.headers.has('Use-Temp-Token');
    const skipAuthRefresh: boolean = request.headers.has('Skip-auth-refresh');
    let newHeaders = request.headers
      .delete('Skip-auth')
      .delete('Use-Temp-Token')
      .delete('Skip-auth-refresh');
    const hasAccept: boolean = request.headers.has('Accept');
    if (!hasAccept) {
      newHeaders = newHeaders.append('Accept', 'application/json');
    }
    const hasContentType: boolean = request.headers.has('Content-Type');
    // A FormData body must keep the multipart Content-Type the browser generates, because only it
    // carries the boundary parameter. Forcing application/json here leaves the server unable to
    // parse the upload at all (multer sees no parts and the request 500s), so let the browser set
    // the header for these requests.
    const isFormData: boolean = request.body instanceof FormData;
    if (!hasContentType && !isFormData) {
      newHeaders = newHeaders.append('Content-Type', 'application/json; charset=utf-8');
    }
    let basicAuthRequest = request.clone({headers: newHeaders});
    const reportAccessToken = this.reportAccessTokenService.get();
    if (useTempToken && reportAccessToken) {
      hasSkipOAuth = true;
      basicAuthRequest = request.clone(basicAuthRequest.clone({
        headers: basicAuthRequest.headers.append('report-pass', `Bearer ${reportAccessToken}`)
      }));
      const project_id = basicAuthRequest.params.get('project_id');

      if (project_id) {
        // Add project id into headers and remove from params
        basicAuthRequest = request.clone(basicAuthRequest.clone({
          headers: basicAuthRequest.headers.append('report-project-id', project_id)
        }));
        basicAuthRequest = basicAuthRequest.clone({
          params: basicAuthRequest.params.delete('project_id'),
        });
      }
    }
    if (!this.inflightAuthRequest) {
      this.inflightAuthRequest = this.authentication.pipe(select(getToken));
    }
    return this.inflightAuthRequest.pipe(
      take(1),
      switchMap((newToken: string) => {
        this.inflightAuthRequest = null;
        const token = this.authenticationService.getAccessToken() || newToken || '';
        if (hasSkipOAuth) {
          return next.handle(basicAuthRequest).pipe(
            catchError((error) => {
              if (useTempToken && this.isUnauthorized(error)) {
                this.reportAccessTokenService.clear();
              }
              return throwError(() => error);
            })
          );
        }
        if (!token) {
          if (localStorage.getItem('currentUser')) {
            // No token held yet: a page load picking up the session this browser had.
            return this.refreshAndRetry(basicAuthRequest, next, null, true);
          }
          return next.handle(basicAuthRequest);
        }
        return next.handle(this.withAccessToken(basicAuthRequest, token)).pipe(
          catchError((error) => {
            if (!this.isUnauthorized(error)) {
              this.notifyRateLimited(error);
              return throwError(() => error);
            }
            if (useTempToken) {
              this.reportAccessTokenService.clear();
              return throwError(() => error);
            }
            if (skipAuthRefresh) {
              return throwError(() => error);
            }
            const latestToken = this.authenticationService.getAccessToken();
            if (latestToken && latestToken !== token) {
              return next.handle(this.withAccessToken(basicAuthRequest, latestToken));
            }
            return this.refreshAndRetry(basicAuthRequest, next, error);
          })
        );
      }),
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.sessionTimeoutService?.updateFromResponse(event);
            this.appVersionService?.observe(event, request.url);
          }
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            this.maintenanceService?.observe(error, request.url);
            this.sessionTimeoutService?.updateFromResponse(error);
            this.appVersionService?.observe(error, request.url);
          }
        }
      })
    );
  }

  private withAccessToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private isUnauthorized(error: any): boolean {
    return error?.status === 401 || /Unauthorized/.test(error?.statusText || '');
  }

  private refreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: any = null,
    restoring = false
  ): Observable<HttpEvent<any>> {
    return this.authenticationService.refreshSession().pipe(
      catchError((error) => this.maintenanceService?.observe(error)
        ? throwError(() => error)
        : this.handleRefreshFailure(originalError || error, restoring)),
      switchMap((response: any) => {
        if (!this.authenticationService.applySessionResponse(response)) {
          return this.handleRefreshFailure(originalError || response, restoring);
        }
        return next.handle(this.withAccessToken(request, response.token));
      })
    );
  }

  /**
   * A 429 carries the server's own sentence about how long to wait. Show that, once per
   * burst - a busy page can trip the quota with a dozen requests at once, and a dozen
   * identical toasts would bury it.
   */
  private lastRateLimitToastAt = 0;

  private notifyRateLimited(error: any): void {
    if (error?.status !== 429) return;
    const now = Date.now();
    if (now - this.lastRateLimitToastAt < 5000) return;
    this.lastRateLimitToastAt = now;
    const retryAfter = Number(error?.error?.retry_after_seconds);
    const message = error?.error?.message
      || (Number.isFinite(retryAfter) && retryAfter > 0
        ? `Too many requests. Try again in ${retryAfter} second(s).`
        : 'Too many requests. Please wait a moment and try again.');
    this.toastr.warning(message);
  }

  /**
   * `restoring`: the refresh was a page load picking up this browser's session. Finding none
   * is what a sign-in without "Remember me" looks like once the browser has been closed, so
   * that goes to the sign-in page without a word; a session that timed out still says why.
   */
  private handleRefreshFailure(error: any, restoring = false): Observable<never> {
    const code = error?.error?.code;
    const timedOut = ['AUTH_SESSION_IDLE_TIMEOUT', 'AUTH_SESSION_ABSOLUTE_TIMEOUT'].includes(code);
    if (!this.router.url.includes('/login') && (!restoring || timedOut)) {
      const message = code === 'AUTH_SESSION_IDLE_TIMEOUT'
        ? 'You were signed out after a period of inactivity. Please sign in again.'
        : code === 'AUTH_SESSION_ABSOLUTE_TIMEOUT'
          ? 'Your session reached its maximum lifetime. Please sign in again.'
          : 'Your session has expired. Please sign in again.';
      this.toastr.error(message);
    }
    this.loaderService.unsubscribe();
    this.authenticationService.setAccessToken(null);
    this.authentication.dispatch(LogoutSuccess());
    this.reportAccessTokenService.clear();
    this.sessionTimeoutService?.clear();
    localStorage.removeItem('currentUser');
    this.router.navigate(['/login'], {queryParams: {returnUrl: this.currentRoute}});
    return throwError(() => error);
  }
}
